import React from 'react';
import { List, ListItem, Action } from '@op/opds.opux';
import { space } from '@op/opds.tokens';
import { OfficeBranchDetails, OtherBankDetails } from '../utils/types';
import { ContactDetails } from './ContactDetails';
import { useTranslation } from 'react-i18next';

interface SearchDetailsProps {
  officeDetails: OfficeBranchDetails;
  handleOfficeChange: (
    branchNumber: string,
    branchOrganizationIdentificationNumber: string
  ) => void;
}

export const SearchDetails: React.FC<SearchDetailsProps> = ({
  officeDetails,
  handleOfficeChange
}) => {
  const { t } = useTranslation();
  const hasOpeningTime = Array.isArray(officeDetails.branchOpeningTime)
    ? officeDetails.branchOpeningTime.some(t => t && t.trim() !== '')
    : !!officeDetails.branchOpeningTime;

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [officeDetails]);

  return (
    <>
      {officeDetails.bankName && (
        <>
          <h2
            className="bankHeading"
            title="branchHeading"
            style={{ fontSize: '2.6rem', lineHeight: '2.8rem' }}
          >
            {officeDetails.bankName}
          </h2>
          <br />
        </>
      )}
      {officeDetails.branchName && (
        <>
          <h3
            className="branchHeading"
            title="branchName"
            style={{ paddingTop: '7px' }}
          >
            {officeDetails.branchName}
          </h3>
        </>
      )}
      <List>
        {officeDetails.address && (
          <ListItem
            className="label-text-position"
            label={t('ddl.kenttaotsikko_osoite')}
          >
            {officeDetails.address}, {officeDetails.postalCode}{' '}
            {officeDetails.postOffice}
          </ListItem>
        )}
        {hasOpeningTime && (
          <ListItem
            className="label-text-position"
            label={t('ddl.kenttaotsikko_aukioloajat')}
          >
            {officeDetails.branchOpeningTime?.join('  ')}
          </ListItem>
        )}
        {officeDetails.openingTimeAdditionalInformation && (
          <ListItem
            className="label-text-position"
            label={t('ddl.kenttaotsikko_lisatietoa')}
          >
            {officeDetails.openingTimeAdditionalInformation}
          </ListItem>
        )}
        {officeDetails.phone && (
          <ListItem
            className="label-text-position"
            label={t('ddl.kenttaotsikko_puhelin')}
          >
            {officeDetails.phone}
          </ListItem>
        )}
        {
          <ListItem
            className="label-text-position"
            label={t('ddl.kenttaotsikko_palvelunumerot')}
          >
            <ContactDetails doc={officeDetails} />
          </ListItem>
        }

        {officeDetails.ceoName && (
          <ListItem
            className="label-text-position"
            label={t('ddl.kenttaotsikko_toimitusjohtaja')}
          >
            {officeDetails.ceoName}
          </ListItem>
        )}
        {officeDetails.bankSiteUrl && (
          <ListItem
            className="label-text-position"
            label={officeDetails.bankSiteLabel}
          >
            <Action
              icon={() => null}
              style={{
                color: '#ee5a00'
              }}
              href={officeDetails.bankSiteUrl}
              onFocus={(e: React.FocusEvent<HTMLElement>) => {
                e.currentTarget.style.outline = '2px solid #0066cc';
                e.currentTarget.style.outlineOffset = '2px';
                e.currentTarget.style.boxShadow = '0 0 0 2px #0066cc';
              }}
              onBlur={(e: React.FocusEvent<HTMLElement>) => {
                e.currentTarget.style.outline = '';
                e.currentTarget.style.outlineOffset = '';
                e.currentTarget.style.boxShadow = '';
              }}
            >
              {officeDetails.hostname}
              {officeDetails.bankSiteUrl}
            </Action>
          </ListItem>
        )}
      </List>
      {officeDetails.otherOffices && officeDetails.otherOffices.length > 0 && (
        <h3
          style={{ fontSize: '2.2rem', lineHeight: '2rem', marginBottom: '2rem' }}
          title="otherOfficesHeading"
        >
          {t('ddl.valiotsikko_muut_konttorit')}
        </h3>
      )}
      <br />
      <List
        border="all"
        indented
        stacked
        style={{
          margin: 0,
          paddingBottom: space.xxs,
          paddingTop: space.xxs
        }}
      >
        {officeDetails.otherOffices?.map((office: OtherBankDetails) =>
          office.branchNumber && office.companyId ? (
            <ListItem
              key={[office.branchNumber, office.companyId].join(':')}
              style={{
                paddingLeft: '0px',
                paddingTop: '4rem',
                paddingBottom: '10rem',
                minHeight: '16rem'
              }}
              className="search-results-listitem"
              onClick={() =>
                handleOfficeChange(
                  office.branchNumber as string,
                  office.companyId as string
                )
              }
            >
              <h3 className="other-office-branchname-bold">
                {office.branchName}
              </h3>
            </ListItem>
          ) : null
        )}
      </List>
    </>
  );
};
